import { entitlementUserKey } from './entitlementUser'
import { tierById } from './catalog'
import { useCatalogStore } from '../store/CatalogStore'

// Feature-module gating shared by the nav, the route guard, the status bar and the axios
// interceptor. Mirrors the backend's EntitlementRules / feature_module seed (CAR-3710): the
// backend is the source of truth and refuses the API call; this is what keeps the UI from
// offering a dead end.

// Plan names and "which plan has this feature" are NOT here — they come from the plan catalog in
// the database (data/catalog.js, store/CatalogStore.js). This file maps UI routes to feature ids,
// which is code by nature: a new feature is new code. Moving features between plans needs no
// change here.

// Sidebar entry → module. Entries not listed (Home, Help, Settings, Training, Admin Tools) are
// always shown.
export const NAV_MODULES = {
  '/adas-intelligence': 'ai_brief',
  '/vinsearch': 'vin_search',
  '/quotes': 'quotes',
  '/workorders': 'work_orders',
  '/invoices': 'invoicing',
  '/clients': 'crm',
  '/products': 'products_catalog',
  '/support': 'support_ticketing',
  '/scheduler': 'scheduler',
  '/reporting': 'reporting_analytics'
}

// Route prefix → module, for PrivateRoute. Prefixes, not exact paths, so the detail pages
// (/quote/123, /workorder/45/photos, /client/7) are covered by the same line as their list.
const ROUTE_MODULES = [
  ['/adas-intelligence', 'ai_brief'],
  ['/vinsearch', 'vin_search'],
  ['/quote', 'quotes'],
  ['/workorder', 'work_orders'],
  ['/invoice', 'invoicing'],
  ['/client', 'crm'],
  ['/product', 'products_catalog'],
  ['/support', 'support_ticketing'],
  ['/scheduler', 'scheduler']
]

// Module → the center flag an admin switches it on with (Settings → Platform Features). A switched
// module needs the flag on as well as the plan: existing centers are on a plan that includes AI, and
// must not see AI Reports until an admin turns it on (CAR-4038). Anything but `true` is off.
const CENTER_SWITCHED_MODULES = {
  ai_brief: 'is_ai_report_enabled'
}

export const moduleForPath = (pathname = '') => {
  const hit = ROUTE_MODULES.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`) || pathname.startsWith(`${prefix}s`)
  )
  return hit ? hit[1] : null
}

/**
 * Why the current user may or may not use a module at the active center — package first, then role:
 *  - 'allowed' — the plan includes it and the user's role may use it
 *  - 'plan'    — the center's plan doesn't include it (offer an upgrade)
 *  - 'role'    — the plan includes it but this user's role may not use it (not an upgrade case)
 *  - 'disabled' — an admin has switched it off for this center (not an upgrade case either)
 *  - 'pending' — not known yet for this user and center (the sidebar and shortcuts hide gated
 *                 entries meanwhile; the route guard lets the page render and re-checks)
 * Admins always may. The backend computes `modules` (plan ∩ role) and `planModules` (plan), and
 * refuses the API call regardless of what the UI shows. `centerDetail` is the active center, for
 * the admin switches; leaving it out skips them.
 */
export const moduleAccess = ({ user, entitlements, ccId, module, centerDetail }) => {
  if (!module || user?.is_admin) return 'allowed'
  const switchFlag = CENTER_SWITCHED_MODULES[module]
  if (switchFlag && centerDetail !== undefined) {
    if (!ccId || centerDetail?.id !== ccId) return 'pending'
    if (centerDetail[switchFlag] !== true) return 'disabled'
  }
  if (!entitlements || !ccId || entitlements.loadedForCcId !== ccId) return 'pending'
  // Entitlements are the plan narrowed by a role, so another user's answer is no answer.
  if (entitlements.loadedForUser != null && entitlements.loadedForUser !== entitlementUserKey(user)) return 'pending'
  if (entitlements.failOpen) return 'allowed'
  if ((entitlements.modules || []).includes(module)) return 'allowed'
  const plan = entitlements.planModules?.length ? entitlements.planModules : entitlements.modules || []
  return plan.includes(module) ? 'role' : 'plan'
}

/**
 * Whether to show an entry or shortcut for a module. Only when it is known to be allowed: showing
 * it while entitlements load made every entry flash on a refresh and then disappear. (A refresh
 * starts from the cached snapshot, so in practice this only waits on the first load after login.)
 */
export const canUseModule = (args) => moduleAccess(args) === 'allowed'

// The backend's refusal codes (EntitlementEnforcementFilter, and the plan limits of 0173) and what to tell the user.
export const BILLING_REFUSAL_CODES = [
  'SUBSCRIPTION_REQUIRED',
  'TRIAL_PAUSED',
  'BILLING_PAST_DUE',
  'ENTITLEMENT_REQUIRED',
  'CLIENT_LIMIT_REACHED',
  'CLIENT_DISABLED',
  'RO_LOCKED',
  'CLIENT_PORTAL_NOT_INCLUDED',
  'FEATURE_DISABLED'
]

/** Plan-limit refusals (0173): the page that made the call explains them in place; the interceptor stays quiet. */
export const PLAN_LIMIT_CODES = ['CLIENT_LIMIT_REACHED', 'CLIENT_DISABLED', 'RO_LOCKED', 'CLIENT_PORTAL_NOT_INCLUDED']

/** The refusal body `{ code, message, requiredTier, summary }` of an axios error, or null when it isn't one. */
export const planLimitRefusalOf = (error) => {
  const data = error?.response?.data
  return data && PLAN_LIMIT_CODES.includes(data.code) ? data : null
}

export const billingRefusalMessage = (data = {}) => {
  switch (data.code) {
    case 'SUBSCRIPTION_REQUIRED':
      return 'This location has no active subscription. Choose a plan in Settings → Billing.'
    case 'TRIAL_PAUSED':
      return 'Trials are paused. Subscribe in Settings → Billing to keep using this location.'
    case 'BILLING_PAST_DUE':
      return 'A payment failed — this location is paused until the payment method is updated in Settings → Billing.'
    case 'ENTITLEMENT_REQUIRED':
      return `This feature is part of ${tierById(useCatalogStore.getState().catalog, data.requiredTier)?.name || 'a higher plan'}. Upgrade in Settings → Billing.`
    case 'CLIENT_LIMIT_REACHED':
    case 'CLIENT_DISABLED':
    case 'RO_LOCKED':
    case 'CLIENT_PORTAL_NOT_INCLUDED':
      // The backend's message already names the plan and what lifts the limit.
      return data.message || 'Your plan doesn’t allow this. See Settings → Billing.'
    case 'FEATURE_DISABLED':
      // An admin switched the feature off for this location (CAR-4038): not something Billing lifts.
      return data.message || 'This feature isn’t turned on for this location. Ask your administrator to turn it on.'
    default:
      return null
  }
}
